import { SolanaTransactionStatus, TransactionData, TransactionRequest, TransactionStatus } from '@/features/Transactions/transactions.types'
import { MOCK_TRANSACTIONS_ENABLED, mockTransactions } from '@/features/Transactions/transactions.mocks'
import { transactionsService } from '@/services'
import { infiniteQueryOptions, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { notFound } from 'next/navigation'

export async function getUserTransactions(params: TransactionRequest): Promise<TransactionData[]> {
  try {
    const transactions = await transactionsService.getList(params)
    return MOCK_TRANSACTIONS_ENABLED ? [...mockTransactions, ...transactions] : transactions
  } catch (error) {
    throw error
  }
}

export const transactionsQueryKey = (params: TransactionRequest) => ['transactions', params] as const

export const createTransactionsQueryOptions = (params: TransactionRequest, enabled: boolean = true) => queryOptions({
  queryKey: transactionsQueryKey(params),
  enabled: enabled,
  queryFn: () => getUserTransactions(params),
  staleTime: 1000 * 60 * 5,
  gcTime: 0,
})

export const createTransactionsInfiniteQueryOptions = (params: TransactionRequest, enabled: boolean = true) => infiniteQueryOptions({
  queryKey: ['transactions', 'infinite', params],
  enabled: enabled,
  queryFn: ({ pageParam }) => getUserTransactions({
    ...params,
    lastTxId: pageParam,
  }),
  initialPageParam: undefined as string | undefined,
  getNextPageParam: (lastPage) => {
    // If we got fewer transactions than requested, we've reached the end
    if (lastPage.length < params.size) {
      return undefined
    }
    // Return the ID of the last transaction for pagination
    return lastPage[lastPage.length - 1]?.id
  },
  staleTime: 1000 * 60 * 5,
  gcTime: 0,
})

export async function getTransactionDetails(txId: string): Promise<TransactionData> {
  if (MOCK_TRANSACTIONS_ENABLED) {
    const mockTransaction = mockTransactions.find((transaction) => transaction.id === txId)
    if (mockTransaction) return mockTransaction
  }

  try {
    return await transactionsService.getDetails<TransactionData>(txId)
  } catch (error) {
    if (error instanceof AxiosError && error.response?.status === 404) {
      notFound()
    }
    throw error
  }
}

export const transactionDetailsQueryKey = (txId: string) => ['transactionDetails', txId] as const

export const createTransactionDetailsQueryOptions = (txId: string, enabled: boolean = true) => queryOptions({
  queryKey: transactionDetailsQueryKey(txId),
  enabled: enabled && !!txId,
  queryFn: () => getTransactionDetails(txId),
  staleTime: 1000 * 60 * 5, // 5 minutes
  gcTime: 1000 * 60 * 10, // 10 minutes
})

type UpdateTransactionParams = {
  transaction: Partial<TransactionData> & { id: string };
  queryParams?: TransactionRequest;
}

export function useUpdateTransaction() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ transaction }: UpdateTransactionParams) => {
      // This mutation doesn't actually call an API, it's just for optimistic updates
      return transaction
    },
    onMutate: async ({ transaction, queryParams }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['transactions'] })

      // Snapshot the previous values for all transaction queries
      const previousQueries = queryClient.getQueriesData({ queryKey: ['transactions'] })

      // Optimistically update all transaction queries
      queryClient.setQueriesData<TransactionData[]>(
        { queryKey: ['transactions'] },
        (old) => {
          if (!old) return old

          // Check if transaction already exists
          const existingIndex = old.findIndex((tx) => tx.id === transaction.id)

          if (existingIndex !== -1) {
            // Update existing transaction
            return old.map((tx, idx) =>
              idx === existingIndex ? { ...tx, ...transaction } : tx
            )
          } else {
            // Add new transaction at the beginning with IN_PROGRESS status
            const newTransaction: TransactionData = {
              id: transaction.id,
              status: TransactionStatus.IN_PROGRESS,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
              solAddress: transaction.solAddress || '',
              splAddress: transaction.splAddress || '',
              solanaStatus: transaction.solanaStatus || SolanaTransactionStatus.PROCESSED,
              solanaTxId: transaction.solanaTxId || '',
              displayDateNotSendingTx: '',
              failedAt: '',
              failedReason: '',
              lastValidBlockHeight: transaction.lastValidBlockHeight || 0,
              validDateTime: new Date().toISOString(),
              data: transaction.data || {},
              ...transaction,
            }
            return [newTransaction, ...old]
          }
        }
      )

      // Also update infinite queries
      queryClient.setQueriesData(
        { queryKey: ['transactions', 'infinite'] },
        (old: any) => {
          if (!old?.pages) return old

          return {
            ...old,
            pages: old.pages.map((page: TransactionData[]) => {
              const existingIndex = page.findIndex((tx) => tx.id === transaction.id)

              if (existingIndex !== -1) {
                return page.map((tx, idx) =>
                  idx === existingIndex ? { ...tx, ...transaction } : tx
                )
              } else if (page === old.pages[0]) {
                // Add to first page only
                const newTransaction: TransactionData = {
                  id: transaction.id,
                  status: TransactionStatus.IN_PROGRESS,
                  createdAt: new Date().toISOString(),
                  updatedAt: new Date().toISOString(),
                  solAddress: transaction.solAddress || '',
                  splAddress: transaction.splAddress || '',
                  solanaStatus: transaction.solanaStatus || SolanaTransactionStatus.PROCESSED,
                  solanaTxId: transaction.solanaTxId || '',
                  displayDateNotSendingTx: '',
                  failedAt: '',
                  failedReason: '',
                  lastValidBlockHeight: transaction.lastValidBlockHeight || 0,
                  validDateTime: new Date().toISOString(),
                  data: transaction.data || {},
                  ...transaction,
                }
                return [newTransaction, ...page]
              }
              return page
            }),
          }
        }
      )

      return { previousQueries }
    },
    onError: (err, variables, context) => {
      // Rollback on error
      if (context?.previousQueries) {
        context.previousQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data)
        })
      }
    },
  })
}
